// Path: ./src/app/pages/CreateStorePage/index.tsx
import {
  Grid,
  Box,
  Typography,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormHelperText,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import { adminApiService } from "../../../utils/api";
import { useParams } from "react-router-dom";

import {
  GfMerchant,
  GfStore,
  UpdateGfStoreDto,
} from "@swagger/typescript-fetch-goflyer";
import { useNavigate } from "react-router-dom";

import { messages } from "../../../locales/messages";
import { useTranslation } from "react-i18next";
import { useMerchants } from "../../../hooks/useMerchants";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { CustomAutocompleteMultiSelect } from "../../components/CustomAutocomplete";
import { useStoreTagOptions } from "../../../hooks";
import {
  SetupPageContainer,
  TextInputField,
} from "../../../utils/commonStyledComponents/commonStyled";
import { useHandleSaveStore } from "./handleSaveStore";
import {
  CancelButton,
  ColorButton,
} from "../../../utils/commonStyledComponents/commonMuiStyled";
import { SectionHeader, SectionBox } from "./styled";
import { extractPlaceAndCoordinates } from "../../../utils/utils";
import { useLoading } from "../../../contextApi/LoadingContext";
import { toastError } from "../../../utils/pageUtil/toast";
import { InputLabelText } from "../../components/AccountPageCreatePageCommon";
import { disableWheelOnInput } from "../../../utils/disableWheelOnInput";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { InputField } from "../../components/TextField";
import { CheckBoxMui } from "../../components/CheckBox";
import {
  buildReleaseDayTime,
  getReleaseDay,
  getReleaseTime,
  isReleaseScheduleIncomplete,
} from "./websiteFlyerRelease";

const RELEASE_DAY_OPTIONS = [
  messages.Sunday,
  messages.Monday,
  messages.Tuesday,
  messages.Wednesday,
  messages.Thursday,
  messages.Friday,
  messages.Saturday,
];

const CreateStorePage = () => {
  // 1. React Router hooks
  const { storeId } = useParams();
  const navigate = useNavigate();
  const handleSaveStore = useHandleSaveStore();
  const { showLoadingScreen } = useLoading();

  // 2. Custom hooks - Localization and Data fetching
  const { t } = useTranslation();
  const { merchantsList, isMerchantOptionLoading, handleOnMerchantSearch } =
    useMerchants();
  const { storeTagOptionList } = useStoreTagOptions();

  // 3. State for Dtos, models and data
  const [updateStore, setUpdateStore] = useState<UpdateGfStoreDto>();
  const [googleMapUrl, setGoogleMapUrl] = useState("");
  const [notificationEmailInput, setNotificationEmailInput] = useState("");
  // websiteFlyerReleaseDayTime is edited as a day-of-week + time-of-day pair;
  // the two halves only become a "<DAY> HH:mm" value once both are filled in.
  const [releaseDay, setReleaseDay] = useState<number | "">("");
  const [releaseTime, setReleaseTime] = useState("");

  const releaseScheduleIncomplete = isReleaseScheduleIncomplete(
    releaseDay,
    releaseTime,
  );

  const handleReleaseScheduleChange = (day: number | "", time: string) => {
    setReleaseDay(day);
    setReleaseTime(time);
    setUpdateStore((prev) => ({
      ...prev,
      websiteFlyerReleaseDayTime: buildReleaseDayTime(day, time) as string,
    }));
  };

  const handleClearReleaseSchedule = () => handleReleaseScheduleChange("", "");

  const hasReleaseSchedule = releaseDay !== "" || !!releaseTime;

  const handleMerchantSelect = (value?: GfMerchant[]) => {
    value &&
      setUpdateStore({
        ...updateStore,
        merchant: value[0],
      });
  };

  const handleAddNotificationEmail = () => {
    const trimmedEmail = notificationEmailInput.trim();

    if (!trimmedEmail) {
      toastError(`${t(messages.Email())} ${t(messages.is_Empty())}`);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toastError(t(messages.Incorrect_Email_please_type_correct_email()));
      return;
    }

    const alreadyExists = (updateStore?.notificationEmails ?? []).some(
      (email) => email.toLowerCase() === trimmedEmail.toLowerCase(),
    );

    if (alreadyExists) {
      toastError(t(messages.EmailAlreadyAdded()));
      return;
    }

    setUpdateStore((prev) => ({
      ...prev,
      notificationEmails: [...(prev?.notificationEmails ?? []), trimmedEmail],
    }));
    setNotificationEmailInput("");
  };

  const handleRemoveNotificationEmail = (emailToRemove: string) => {
    setUpdateStore((prev) => ({
      ...prev,
      notificationEmails: (prev?.notificationEmails ?? []).filter(
        (email) => email.toLowerCase() !== emailToRemove.toLowerCase(),
      ),
    }));
  };

  const handleCancel = async () => {
    navigate(-1);
  };

  const getStoreDetails = async () => {
    showLoadingScreen(true);
    const data = extractPlaceAndCoordinates(googleMapUrl);

    if (!data?.place) {
      toastError(t(messages.PlaceNameNotExist()));
      showLoadingScreen(false);
      return;
    }
    const response =
      await adminApiService.gfStoreAdminControllerGetStoreDetailsUsingGoogleFindPlaceApi(
        data.place,
        data.latitude,
        data.longitude,
      );

    if (!response?.storeInfo) {
      toastError(t(messages.UnableToExtractStoreDetails()));
      showLoadingScreen(false);
      return;
    }

    setUpdateStore((prev) => ({
      ...prev,
      ...response.storeInfo,
      googlePlaceUrl: googleMapUrl,
    }));

    showLoadingScreen(false);
  };
  useEffect(() => {
    if (storeId) {
      (async function () {
        const store =
          (await adminApiService.getOneBaseGfStoreAdminControllerGfStore(
            storeId,
          )) as GfStore;
        setUpdateStore(store);
        store &&
          store?.googlePlaceUrl &&
          setGoogleMapUrl(store?.googlePlaceUrl);
        setReleaseDay(getReleaseDay(store?.websiteFlyerReleaseDayTime));
        setReleaseTime(getReleaseTime(store?.websiteFlyerReleaseDayTime));
      })();
    }
  }, []);

  return (
    <SetupPageContainer data-testid="create-store-page">
      <AccountCardWrapper
        title={
          updateStore?.id
            ? t(messages.Edit())
            : t(messages.Create()) + " " + t(messages.stores())
        }
      >
        <Box
          sx={{
            maxWidth: "900px",
            margin: "auto",
            padding: { xs: "5px", sm: "10px", md: "24px" },
          }}
        >
          {/* Store Location Section */}
          <SectionBox>
            <SectionHeader variant="h6">
              {t(messages.STORE_LOCATION())}
            </SectionHeader>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <InputLabelText>
                  {t(messages.GoogleMapStoreURL())}
                </InputLabelText>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <TextInputField
                    value={googleMapUrl}
                    type="text"
                    style={{ flex: 1, marginBottom: "0px" }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setGoogleMapUrl(event.target.value);
                    }}
                  />
                  <ColorButton
                    variant="contained"
                    size="small"
                    sx={{ fontSize: "16px", minWidth: "140px" }}
                    onClick={async () => {
                      getStoreDetails();
                    }}
                  >
                    {t(messages.GetDetails())}
                  </ColorButton>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.longitude())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.location?.coordinates[0]}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-longitude-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => {
                      const coordinates = prev?.location
                        ? prev.location.coordinates
                        : [];
                      coordinates[0] = parseFloat(event.target.value);
                      return {
                        ...prev,
                        location: { type: "Point", coordinates },
                      };
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.latitude())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.location?.coordinates[1]}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-latitude-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => {
                      const coordinates = prev?.location
                        ? prev.location.coordinates
                        : [];
                      coordinates[1] = parseFloat(event.target.value);
                      return {
                        ...prev,
                        location: { type: "Point", coordinates },
                      };
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Address())}</InputLabelText>
                <TextInputField
                  value={updateStore?.address}
                  type="text"
                  data-testid="create-store-address-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      address: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.City())}</InputLabelText>
                <TextInputField
                  value={updateStore?.city}
                  type="text"
                  data-testid="create-store-city-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      city: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Province())}</InputLabelText>
                <TextInputField
                  value={updateStore?.province}
                  type="text"
                  data-testid="create-store-province-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      province: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Country())}</InputLabelText>
                <TextInputField
                  value={updateStore?.country}
                  type="text"
                  data-testid="create-store-country-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      country: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Street())}</InputLabelText>
                <TextInputField
                  value={updateStore?.street}
                  type="text"
                  data-testid="create-store-street-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      street: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Zip())}</InputLabelText>
                <TextInputField
                  value={updateStore?.zip}
                  type="text"
                  data-testid="create-store-zip-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      zip: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <InputLabelText>{t(messages.GoogleMapUrl())}</InputLabelText>
                <TextInputField
                  value={updateStore?.googleMapUrl}
                  type="text"
                  data-testid="create-store-google-map-url-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      googleMapUrl: event.target.value,
                    }));
                  }}
                />
              </Grid>
            </Grid>
          </SectionBox>

          {/* Business Information Section */}
          <SectionBox>
            <SectionHeader variant="h6">
              {t(messages.BUSINESS_INFORMATION())}
            </SectionHeader>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.Phone_Number())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.phone}
                  type="text"
                  data-testid="create-store-phone-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      phone: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Signature())}</InputLabelText>
                <TextInputField
                  value={updateStore?.signature}
                  type="text"
                  data-testid="create-store-signature-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      signature: event.target.value,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <InputLabelText>
                    {t(messages.Notification_Emails())}
                  </InputLabelText>
                  <Tooltip
                    title={t(messages.NotificationEmailsTooltip())}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      aria-label={t(messages.NotificationEmailsTooltip())}
                      sx={{
                        color: "grey",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(245, 130, 32, 0.08)",
                        },
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <InputField
                    value={notificationEmailInput}
                    type="email"
                    placeholder={t(messages.Email())}
                    data-testid="create-store-notification-email-input"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setNotificationEmailInput(event.target.value);
                    }}
                    onKeyDown={(
                      event: React.KeyboardEvent<HTMLInputElement>,
                    ) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleAddNotificationEmail();
                      }
                    }}
                  />
                  <ColorButton
                    variant="contained"
                    size="small"
                    sx={{ minWidth: { xs: "100%", sm: "160px" } }}
                    onClick={handleAddNotificationEmail}
                    disabled={!notificationEmailInput.trim()}
                    data-testid="create-store-add-notification-email-button"
                  >
                    {t(messages.Add_Email())}
                  </ColorButton>
                </Box>
                {!!updateStore?.notificationEmails?.length && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    {updateStore.notificationEmails?.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        color="primary"
                        variant="outlined"
                        onDelete={() => handleRemoveNotificationEmail(email)}
                        data-testid="create-store-notification-email-chip"
                        sx={{
                          maxWidth: "100%",
                          borderColor: "#f58220",
                          color: "#f58220",
                          fontWeight: 500,
                          "& .MuiChip-deleteIcon": {
                            color: "#f58220",
                            transition: "color 0.2s ease-in-out",
                          },
                          "& .MuiChip-deleteIcon:hover": {
                            color: "#d32f2f",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(245, 130, 32, 0.08)",
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <InputLabelText>
                  {t(messages.merchant())} ({t(messages.required())})
                </InputLabelText>
                <CustomAutocompleteMultiSelect
                  optionsList={merchantsList?.map((merchant) => ({
                    value: merchant,
                    label: merchant.name,
                  }))}
                  isLoading={isMerchantOptionLoading}
                  selectedValues={
                    (() => {
                      if (updateStore?.merchant) {
                        return [
                          {
                            value: updateStore.merchant,
                            label: updateStore.merchant.name,
                          },
                        ];
                      }
                      return [];
                    })() as {
                      value: GfMerchant;
                      label: string;
                    }[]
                  }
                  multiSelect={false}
                  handleOnChange={handleMerchantSelect}
                  handleOnSearch={handleOnMerchantSearch}
                  dataTestId="create-store-merchant-select"
                />
              </Grid>

              <Grid item xs={12}>
                <InputLabelText>{t(messages.Store_Tag())}</InputLabelText>
                <CustomAutocompleteMultiSelect
                  optionsList={storeTagOptionList?.map((fic) => ({
                    value: fic,
                    label: fic.name,
                  }))}
                  selectedValues={updateStore?.storeTag?.map((tag) => ({
                    value: tag,
                    label: tag.name,
                  }))}
                  multiSelect={true}
                  isLoading={false}
                  handleOnChange={(value) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      storeTag: value,
                    }));
                  }}
                  WrapperCss={`
                      flex-direction: row;
                      align-items: center;
                      justify-content: space-between;
                      width: 100%;
                      @media (max-width: 457px) {
                        flex-direction: column;
                        align-items: start;
                        justify-content: start;
                      }
                      `}
                  dataTestId="create-store-category-select"
                />
              </Grid>

              <Grid item xs={12}>
                <InputLabelText>{t(messages.DISCLAIMER_TEXT())}</InputLabelText>
                <textarea
                  value={updateStore?.disclaimerText || ""}
                  rows={3}
                  data-testid="create-store-disclaimer-text-input"
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      disclaimerText: event.target.value,
                    }));
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                    resize: "vertical",
                    minHeight: "80px",
                  }}
                />
              </Grid>
            </Grid>
          </SectionBox>

          {/* Store Ranking Section */}
          <SectionBox>
            <SectionHeader variant="h6">{t(messages.Rank())}</SectionHeader>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <InputLabelText>{t(messages.Rank())}</InputLabelText>
                  <Tooltip
                    title={t(messages.Rank_Tooltip())}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: "grey",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(245, 130, 32, 0.08)",
                        },
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextInputField
                  value={updateStore?.rank ?? ""}
                  type="number"
                  min={1}
                  max={20}
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-rank-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const val = parseFloat(event.target.value);
                    setUpdateStore((prev) => ({
                      ...prev,
                      rank: isNaN(val)
                        ? undefined
                        : Math.min(20, Math.max(1, val)),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <InputLabelText>
                    {t(messages.Rank_Radius_Limit())}
                  </InputLabelText>
                  <Tooltip
                    title={t(messages.Rank_Radius_Limit_Tooltip())}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      sx={{
                        color: "grey",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(245, 130, 32, 0.08)",
                        },
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextInputField
                  value={updateStore?.rankRadiusLimit ?? ""}
                  type="number"
                  min={1}
                  max={100}
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-rank-radius-limit-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const val = parseFloat(event.target.value);
                    setUpdateStore((prev) => ({
                      ...prev,
                      rankRadiusLimit: isNaN(val)
                        ? undefined
                        : Math.min(100, Math.max(1, val)),
                    }));
                  }}
                />
              </Grid>
            </Grid>
          </SectionBox>

          {/* Operating Hours Section */}
          <SectionBox>
            <SectionHeader variant="h6">
              {t(messages.Operating_Hours())}
            </SectionHeader>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  {t(messages.WEEKDAY_HOURS())}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.Open_Time())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.openTime}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-open-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      openTime: parseFloat(event.target.value),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.Close_Time())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.closeTime}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-close-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      closeTime: parseFloat(event.target.value),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  {t(messages.SATURDAY_HOURS())}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.SATURDAY_OPEN_TIME())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.saturdayOpenTime}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-saturday-open-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      saturdayOpenTime: parseFloat(event.target.value),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.SATURDAY_CLOSE_TIME())}({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.saturdayCloseTime}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-saturday-close-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      saturdayCloseTime: parseFloat(event.target.value),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{
                    fontWeight: "bold",
                    textDecoration: "underline",
                  }}
                >
                  {t(messages.SUNDAY_HOURS())}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.SUNDAY_OPEN_TIME())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.sundayOpenTime}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-sunday-open-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      sundayOpenTime: parseFloat(event.target.value),
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>
                  {t(messages.SUNDAY_CLOSE_TIME())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateStore?.sundayClosedTime}
                  type="number"
                  onWheel={disableWheelOnInput}
                  data-testid="create-store-sunday-close-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      sundayClosedTime: parseFloat(event.target.value),
                    }));
                  }}
                />
              </Grid>
            </Grid>
          </SectionBox>

          <SectionBox>
            <SectionHeader variant="h6">
              {t(messages.WEBSITE_FLYER_DISPLAY())}
            </SectionHeader>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CheckBoxMui
                  title={t(messages.Website_Flyer_Display_Enabled())}
                  tooltip={t(messages.Website_Flyer_Display_Enabled_Tooltip())}
                  value={!!updateStore?.websiteFlyerDisplayEnabled}
                  dataTestId="create-store-website-flyer-display-enabled-checkbox"
                  onChange={(_event, checked) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      websiteFlyerDisplayEnabled: checked,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <CheckBoxMui
                  title={t(messages.Website_Flyer_Latest_Only())}
                  tooltip={t(messages.Website_Flyer_Latest_Only_Tooltip())}
                  value={!!updateStore?.websiteFlyerLatestOnly}
                  dataTestId="create-store-website-flyer-latest-only-checkbox"
                  onChange={(_event, checked) => {
                    setUpdateStore((prev) => ({
                      ...prev,
                      websiteFlyerLatestOnly: checked,
                    }));
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <InputLabelText>
                    {t(messages.Website_Flyer_Release_Day_Time())}
                  </InputLabelText>
                  <Tooltip
                    title={t(messages.Website_Flyer_Release_Day_Time_Tooltip())}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      size="small"
                      aria-label={t(
                        messages.Website_Flyer_Release_Day_Time_Tooltip(),
                      )}
                      sx={{
                        color: "grey",
                        p: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(245, 130, 32, 0.08)",
                        },
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    startIcon={<ClearIcon fontSize="small" />}
                    onClick={handleClearReleaseSchedule}
                    disabled={!hasReleaseSchedule}
                    data-testid="create-store-website-flyer-clear-release-schedule-button"
                    sx={{
                      textTransform: "capitalize",
                      color: "#f58220",
                      "&:hover": {
                        backgroundColor: "rgba(245, 130, 32, 0.08)",
                      },
                    }}
                  >
                    {t(messages.Clear_Release_Schedule())}
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Release_Day())}</InputLabelText>
                <Select
                  value={releaseDay}
                  displayEmpty
                  size="small"
                  fullWidth
                  error={releaseScheduleIncomplete}
                  data-testid="create-store-website-flyer-release-day-select"
                  onChange={(event) => {
                    const value = event.target.value;
                    // "No schedule" means no schedule at all, so it drops the
                    // time too rather than leaving a dangling half.
                    if (value === "") {
                      handleClearReleaseSchedule();
                      return;
                    }
                    handleReleaseScheduleChange(Number(value), releaseTime);
                  }}
                  sx={{ maxWidth: "380px" }}
                >
                  <MenuItem value="">{t(messages.No_Schedule())}</MenuItem>
                  {RELEASE_DAY_OPTIONS.map((day, index) => (
                    <MenuItem key={index} value={index}>
                      {t(day())}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabelText>{t(messages.Release_Time())}</InputLabelText>
                <TextInputField
                  value={releaseTime}
                  type="time"
                  data-testid="create-store-website-flyer-release-time-input"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleReleaseScheduleChange(releaseDay, event.target.value);
                  }}
                />
              </Grid>

              {releaseScheduleIncomplete && (
                <Grid item xs={12}>
                  <FormHelperText error>
                    {t(messages.Release_Day_And_Time_Both_Required())}
                  </FormHelperText>
                </Grid>
              )}
            </Grid>
          </SectionBox>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <CancelButton
              variant="contained"
              onClick={handleCancel}
              data-testid="create-store-cancel-button"
            >
              {t(messages.Cancel())}
            </CancelButton>
            <ColorButton
              variant="contained"
              onClick={async () => {
                if (releaseScheduleIncomplete) {
                  toastError(t(messages.Release_Day_And_Time_Both_Required()));
                  return;
                }
                await handleSaveStore(updateStore);
              }}
              data-testid="create-store-finish-button"
            >
              {updateStore?.id ? t(messages.Update()) : t(messages.Finish())}
            </ColorButton>
          </Box>
        </Box>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default CreateStorePage;
